import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Download, Loader2, Search, Table2 } from 'lucide-react';
import { toast } from 'sonner';
import { monthLabel, type TrendStatus } from '@afiliados/shared';
import { PageHeader } from '@/components/layout/PageHeader';
import { MatrixTable, TrendLegend, type Metric } from '@/components/MatrixTable';
import { MetricToggle, MonthRange } from '@/components/MonthControls';
import { DataTablePagination } from '@/components/DataTablePagination';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { reportsApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { monthLong } from '@/lib/format';
import { useCan } from '@/features/auth/store';

const PAGE_SIZE = 50;

/** "2026-04" → "2026-03" */
function previousMonthKey(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
}

/** O Planilhão: afiliados × meses, posição pelo líquido, % e acumulado, cores de variação. */
export function MonthlyPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const canExport = useCan('reports.export');
  const months = useQuery({ queryKey: ['months'], queryFn: reportsApi.months, staleTime: 60_000 });
  const available = months.data ?? [];

  const from = params.get('from') ?? available[0] ?? '';
  const to = params.get('to') ?? available[available.length - 1] ?? '';
  const by = (params.get('by') ?? 'affiliate') as 'affiliate' | 'product';
  const metric = (params.get('metric') ?? 'both') as Metric;
  const search = params.get('q') ?? '';
  const onlyParam = params.get('only');
  const only = onlyParam === 'active' || onlyParam === 'alert' ? onlyParam : 'all';
  const sortBy = params.get('sort') ?? 'netSales';
  const sortDir = params.get('dir') ?? 'desc';
  const heat = params.get('heat') !== '0';
  /** Métrica da tendência: acompanha a métrica exibida; com "Ambos", o líquido. */
  const trendMetric: 'gross' | 'net' = metric === 'gross' ? 'gross' : 'net';
  const page = Number(params.get('page') ?? 1);
  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => setSearchInput(search), [search]);

  const set = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined || v === '') next.delete(k);
      else next.set(k, v);
    }
    if (!('page' in patch)) next.delete('page');
    setParams(next, { replace: true });
  };

  const query = {
    from,
    to,
    by,
    search: search || undefined,
    only,
    sortBy,
    sortDir,
    trendMetric,
    page,
    pageSize: PAGE_SIZE,
  };
  const report = useQuery({
    queryKey: ['reports', 'monthly', query],
    queryFn: () => reportsApi.monthly(query),
    enabled: !!from && !!to,
    placeholderData: (p) => p,
  });
  const exportMut = useMutation({
    mutationFn: () =>
      reportsApi.download(
        'monthly',
        { ...query, metric, heat: heat ? '1' : '0', page: 1 },
        'planilhao.xlsx',
      ),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Falha ao exportar'),
  });

  const r = report.data;
  const coverage = new Map((r?.coverage ?? []).map((c) => [c.month, c]));
  const columns = (r?.months ?? []).map((m, i, all) => {
    const cov = coverage.get(m);
    const prev = i > 0 ? all[i - 1]! : previousMonthKey(m);
    return {
      key: m,
      label: monthLabel(m),
      hint: cov?.partial
        ? `${monthLong(m)}: ${cov.daysCovered} de ${cov.daysInMonth} dias importados. A variação usa a projeção para o mês inteiro.`
        : monthLong(m),
      partial: cov?.partial ?? false,
      prevLabel: monthLabel(prev),
    };
  });
  const alertCount = r?.items.filter((i) => {
    const st: TrendStatus | null = i.lastTrend[trendMetric].status;
    return st === 'down' || st === 'down_strong' || st === 'zero';
  }).length;
  const entityWord = by === 'product' ? 'produto' : 'afiliado';

  return (
    <div>
      <PageHeader
        title="Faturamento mensal"
        description="O Planilhão: faturamento bruto e líquido de cada afiliado, mês a mês, com posição, participação e a variação contra o mês anterior"
        actions={
          canExport && (
            <Button
              variant="outline"
              onClick={() => exportMut.mutate()}
              disabled={exportMut.isPending || !r?.total}
            >
              {exportMut.isPending ? <Loader2 className="animate-spin" /> : <Download />} Exportar
              Excel
            </Button>
          )
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <MonthRange
          months={available}
          from={from}
          to={to}
          onChange={(f, t) => set({ from: f, to: t })}
        />
        <div className="space-y-1">
          <Label className="text-xs">Agrupar por</Label>
          <Select value={by} onValueChange={(v) => set({ by: v })}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="affiliate">Afiliado</SelectItem>
              <SelectItem value="product">Produto</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <MetricToggle value={metric} onChange={(m) => set({ metric: m })} />
        <div className="space-y-1">
          <Label className="text-xs">Ordenar por</Label>
          <Select
            value={`${sortBy}:${sortDir}`}
            onValueChange={(v) => {
              const [s, d] = v.split(':');
              set({ sort: s, dir: d });
            }}
          >
            <SelectTrigger className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="netSales:desc">Maior líquido</SelectItem>
              <SelectItem value="netSales:asc">Menor líquido</SelectItem>
              <SelectItem value="grossSales:desc">Maior bruto</SelectItem>
              <SelectItem value="grossSales:asc">Menor bruto</SelectItem>
              <SelectItem value="trend:asc">Maior queda no último mês</SelectItem>
              <SelectItem value="trend:desc">Maior alta no último mês</SelectItem>
              <SelectItem value="name:asc">Nome (A–Z)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Mostrar</Label>
          <Select value={only} onValueChange={(v) => set({ only: v === 'all' ? undefined : v })}>
            <SelectTrigger className="w-60">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="active">Só com faturamento</SelectItem>
              <SelectItem value="alert">Só em alerta (queda ou zerou)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-56 flex-1 space-y-1">
          <Label className="text-xs" htmlFor="monthly-q">
            Buscar
          </Label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
            <Input
              id="monthly-q"
              placeholder={by === 'product' ? 'Nome do produto' : 'Nome do afiliado'}
              className="pl-8"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && set({ q: searchInput.trim() })}
              onBlur={() => searchInput.trim() !== search && set({ q: searchInput.trim() })}
            />
          </div>
        </div>
        <div className="flex items-center gap-2 pb-2">
          <Switch
            id="heat"
            checked={heat}
            onCheckedChange={(v) => set({ heat: v ? undefined : '0' })}
          />
          <Label htmlFor="heat" className="text-xs">
            Cores de variação
          </Label>
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        {months.isLoading || (report.isLoading && !r) ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} className="h-9" />
            ))}
          </div>
        ) : !available.length ? (
          <div className="p-6">
            <EmptyState
              icon={Table2}
              title="Nenhum relatório importado"
              description="Importe os relatórios mensais da BuyGoods em Importações para montar o Planilhão."
            />
          </div>
        ) : r ? (
          <>
            <MatrixTable
              columns={columns}
              rows={r.items}
              totals={r.totals}
              metric={metric}
              entityLabel={by === 'product' ? 'Produto' : 'Afiliado'}
              heat={heat}
              trendMetric={trendMetric}
              onRowClick={
                by === 'affiliate'
                  ? (row) => navigate(`/afiliados/${row.id}?from=${from}&to=${to}`)
                  : undefined
              }
              emptyMessage={
                only === 'alert'
                  ? 'Ninguém em alerta no último mês do intervalo.'
                  : search
                    ? `Nenhum ${entityWord} com "${search}".`
                    : 'Nenhuma linha no período.'
              }
            />
            {heat && <TrendLegend className="border-t px-4 py-2" />}
            <div className="flex flex-wrap items-center justify-between gap-2 border-t px-2">
              <p className="px-2 text-xs text-muted-foreground">
                {r.totals.entities} {entityWord}(s) no período · {r.totals.entitiesActive} com
                faturamento
                {alertCount !== undefined &&
                  only !== 'alert' &&
                  ` · ${alertCount} em alerta nesta página`}
                {' · posição e % calculados sobre o conjunto inteiro'}
              </p>
              <DataTablePagination
                page={r.page}
                pageSize={r.pageSize}
                total={r.total}
                onPageChange={(p) => set({ page: String(p) })}
              />
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
